import React, { useState, ReactNode } from "react";
import styled from "styled-components";
import { Tooltip as UiTooltip } from "../Tooltip";
import { isPositiveStatus, formatDateTime } from "../../utils";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";
import OnamNotice, { splitMessage, NoticePara } from "../../onam/chrome/OnamNotice";

export type PqsRow = {
  orgName: string;
  bucketName: string;
  status: string;
  checkedAt: string;
};

type InfoPopupProps = {
  Icon?: any;
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  lastRefreshedAt?: string | null;
  displayText?: string;
  headerText?: string;
  iconOnClick?: (text: string) => void;
  closeButtonLabel?: string;
  showCancelButton?: boolean;
  onCancelButtonClick?: () => void;
  freeText?: boolean;
  children?: ReactNode;
  tiles?: {
    label: string;
    status?: string;
    lastChecked?: string;
  }[];
  tableData?: { col1: string; col2: string; col3: string }[];
  tableHeaders?: { col1?: string; col2?: string; col3?: string };
  title?: string;
  pqsData?: PqsRow[];
  topRight?: boolean;
  onPqsTabClick?: () => void;
  danger?: boolean;
  warning?: boolean;
  error?: boolean;
  contentWidth?: string | number;
};

function renderFormattedText(text: string) {
  if (!text) return <p>{text}</p>;

  const trimmed = text.trim();

  // Pure JSON
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      return <JsonPre>{JSON.stringify(parsed, null, 2)}</JsonPre>;
    } catch {}
  }

  // Text with embedded JSON — pick whichever of { or [ appears first
  const braceIdx = trimmed.indexOf("{");
  const bracketIdx = trimmed.indexOf("[");

  let jsonStartIdx = -1;
  let openChar: string;
  let closeChar: string;

  if (braceIdx === -1 && bracketIdx === -1) {
    return <p>{text}</p>;
  } else if (braceIdx === -1 || (bracketIdx !== -1 && bracketIdx < braceIdx)) {
    jsonStartIdx = bracketIdx;
    openChar = "[";
    closeChar = "]";
  } else {
    jsonStartIdx = braceIdx;
    openChar = "{";
    closeChar = "}";
  }

  const before = trimmed.substring(0, jsonStartIdx);
  const rest = trimmed.substring(jsonStartIdx);
  let depth = 0;
  let jsonEnd = -1;
  let inString = false;
  let escape = false;

  for (let i = 0; i < rest.length; i++) {
    const ch = rest[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\" && inString) {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === openChar) depth++;
    else if (ch === closeChar) {
      depth--;
      if (depth === 0) {
        jsonEnd = i;
        break;
      }
    }
  }

  if (jsonEnd > -1) {
    const jsonStr = rest.substring(0, jsonEnd + 1);
    const after = rest.substring(jsonEnd + 1).trim();
    try {
      const parsed = JSON.parse(jsonStr);
      return (
        <>
          {before && (
            <p style={{ margin: 0, marginBottom: 6, whiteSpace: "pre-wrap" }}>
              {before}
            </p>
          )}
          <JsonPre>{JSON.stringify(parsed, null, 2)}</JsonPre>
          {after && (
            <p style={{ margin: 0, marginTop: 6, whiteSpace: "pre-wrap" }}>
              {after}
            </p>
          )}
        </>
      );
    } catch {}
  }

  return <TextPre>{text}</TextPre>;
}

const InfoPopup: React.FC<InfoPopupProps> = ({
  Icon,
  isOpen,
  onClose,
  onRefresh,
  isLoading,
  lastRefreshedAt,
  displayText,
  headerText,
  iconOnClick,
  closeButtonLabel,
  showCancelButton,
  onCancelButtonClick,
  freeText,
  children,
  tiles,
  tableData,
  tableHeaders,
  title,
  pqsData,
  topRight,
  onPqsTabClick,
  danger,
  warning,
  error,
  contentWidth,
}) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<"fqs" | "pqs">("fqs");
  const showTabs = pqsData !== undefined;

  const handleIconClick = (text: string) => {
    if (iconOnClick) {
      iconOnClick(text);
      setTooltipVisible(true);
      setTimeout(() => setTooltipVisible(false), 2000);
    }
  };

  const handleDismiss = showCancelButton ? onCancelButtonClick : onClose;

  if (!isOpen) return null;
  // ONAM skin: plain-text notices and questions use ONAM's dialog; the richer uses below keep this one.
  if (FEATURES.curationSkin && !children && !tiles?.length && !tableData?.length && pqsData === undefined && !onRefresh && !Icon) {
    const kind = error || danger || /error/i.test(headerText || "") ? "error" : warning ? "warning" : "info";
    const label = (headerText || title || (kind === "error" ? "Error" : kind === "warning" ? "Warning" : "Information")).trim();
    const { title: heading, rest } = splitMessage(displayText, label);
    return (
      <OnamNotice
        kind={kind}
        label={label}
        title={heading}
        primary={closeButtonLabel || (showCancelButton ? "Confirm" : "Close")}
        onPrimary={onClose}
        cancel={showCancelButton ? "Cancel" : undefined}
        onCancel={showCancelButton ? onCancelButtonClick : undefined}
        danger={danger}
      >
        {rest.length ? rest.map((p, i) => <NoticePara key={i} text={p} json={renderFormattedText} />) : null}
      </OnamNotice>
    );
  }
  return (
    <ModalBackdrop onClick={handleDismiss} topRight={topRight}>
      <ModalContent onClick={(e) => e.stopPropagation()} topRight={topRight} contentWidth={contentWidth}>
        <PopupHeader warning={warning} error={error}>
          <PopupHeaderSpacer />
          <PopupTitle warning={warning} error={error}>
            {warning && <span style={{ marginRight: 6 }}>{FEATURES.curationSkin ? <OI n="warn" size="1em" /> : "⚠"}</span>}
            {title || headerText || " "}
          </PopupTitle>
          <PopupHeaderActions>
            {onRefresh && activeTab === "fqs" && (
              <UiTooltip text="Refresh" placement="bottom">
              <RefreshButton
                onClick={onRefresh}
                warning={warning}
              >
                {FEATURES.curationSkin ? <OI n="refresh" size={16} /> : (
<svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </svg>
)}
              </RefreshButton>
              </UiTooltip>
            )}
            <CloseXButton
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss?.();
              }}
              aria-label="Close"
              warning={warning} error={error}
            >
              {(FEATURES.curationSkin ? <OI n="close" size="1em" /> : "✕")}
            </CloseXButton>
          </PopupHeaderActions>
        </PopupHeader>

        <PopupBody>
          {isLoading && (
            <LoadingOverlay>
              <LoadingSpinner />
              <LoadingText>Refreshing...</LoadingText>
            </LoadingOverlay>
          )}
          {children ? (
            children
          ) : (
            <>
              {lastRefreshedAt && activeTab === "fqs" && (
                <LastRefreshedLabel>
                  Last Refreshed At: {formatDateTime(lastRefreshedAt)}
                </LastRefreshedLabel>
              )}

              {showTabs && (
                <TabRow>
                  <TabButton
                    active={activeTab === "fqs"}
                    onClick={() => setActiveTab("fqs")}
                  >
                    FQS
                  </TabButton>
                  <TabButton
                    active={activeTab === "pqs"}
                    onClick={() => {
                      setActiveTab("pqs");
                      onPqsTabClick?.();
                    }}
                  >
                    PQS
                  </TabButton>
                </TabRow>
              )}

              {(!showTabs || activeTab === "fqs") && (
                <AnimatedTabContent key="fqs">
                  {tiles && tiles.length > 0 && (
                    <TilesWrapper>
                      {tiles?.map((tile, idx) => (
                        <Tile
                          key={idx}
                          style={{
                            background: isPositiveStatus(tile.status)
                              ? "#e8f5e9"
                              : "#fdecea",
                            cursor: "default",
                          }}
                        >
                          <div className="tile-header-row">
                            <span className="tile-title">{tile.label}</span>
                            <span
                              style={{
                                color: isPositiveStatus(tile.status)
                                  ? "#2e7d32"
                                  : "#c62828",
                                fontWeight: 600,
                                fontSize: 13,
                              }}
                            >
                              {isPositiveStatus(tile.status)
                                ? "▲ Up"
                                : "▼ Down"}
                            </span>
                          </div>
                          <div
                            className="tile-date"
                            style={{ fontSize: 11, color: "#666" }}
                          >
                            Latency: {tile.lastChecked}
                          </div>
                        </Tile>
                      ))}
                    </TilesWrapper>
                  )}

                  {!tiles?.length && !tableData?.length && (
                    <>
                      <InputContainer freeText={freeText}>
                        {renderFormattedText(displayText)}
                        {Icon && (
                          <IconWrapper>
                            <img
                              src={Icon}
                              alt="Copy"
                              onClick={() => handleIconClick(displayText)}
                            />
                            {tooltipVisible && <Tooltip>Copied!</Tooltip>}
                          </IconWrapper>
                        )}
                      </InputContainer>
                    </>
                  )}

                  {tableData && tableData.length > 0 && (
                    <TableWrapper>
                      <table>
                        <thead>
                          <tr>
                            <th>{tableHeaders?.col1 ?? "CloudFront"}</th>
                            <th>{tableHeaders?.col2 ?? "Status"}</th>
                            <th>{tableHeaders?.col3 ?? "Healthy"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tableData.map((row, idx) => {
                            const positive = isPositiveStatus(row.col3);
                            return (
                              <tr key={idx}>
                                <td style={{ color: "#000" }}>{row.col1}</td>
                                <td style={{ color: "#000" }}>{row.col2}</td>
                                <td
                                  style={{
                                    color: positive ? "#2e7d32" : "#c62828",
                                    fontWeight: 500,
                                  }}
                                >
                                  {row.col3 === "true"
                                    ? "Yes"
                                    : row.col3 === "false"
                                      ? "No"
                                      : row.col3}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </TableWrapper>
                  )}
                </AnimatedTabContent>
              )}

              {showTabs && activeTab === "pqs" && (
                <AnimatedTabContent key="pqs">
                  <TilesWrapper>
                    {pqsData.map((row, idx) => (
                      <PqsTile
                        key={idx}
                        style={{
                          background: isPositiveStatus(row.status)
                            ? "#e8f5e9"
                            : "#fdecea",
                        }}
                      >
                        <div className="tile-header-row">
                          <span className="tile-title">
                            {row.orgName || "-"}
                          </span>
                          <span
                            style={{
                              color: isPositiveStatus(row.status)
                                ? "#2e7d32"
                                : "#c62828",
                              fontWeight: 600,
                              fontSize: 13,
                            }}
                          >
                            {isPositiveStatus(row.status) ? "▲ Up" : "▼ Down"}
                          </span>
                        </div>
                        <div className="tile-row">
                          <strong>Bucket:</strong> {row.bucketName}
                        </div>
                        <div style={{ height: 4 }} />
                        <div className="tile-row">
                          <strong>Last Checked At:</strong>{" "}
                          {formatDateTime(row.checkedAt)}
                        </div>
                      </PqsTile>
                    ))}
                  </TilesWrapper>
                </AnimatedTabContent>
              )}

            {showCancelButton && (
              <ButtonContainer>
                <CancelButton onClick={onCancelButtonClick}>Cancel</CancelButton>
                <ConfirmButton onClick={onClose} danger={danger}>
                  {closeButtonLabel || "Confirm"}
                </ConfirmButton>
              </ButtonContainer>
            )}
          </>
        )}
        </PopupBody>
      </ModalContent>
    </ModalBackdrop>
  );
};

export default InfoPopup;

const AnimatedTabContent = styled.div`
  @keyframes tab-fade-in {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  animation: tab-fade-in 0.2s ease;
`;

const TabRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 0;
  margin-bottom: 16px;
  border-bottom: 2px solid #e0e0e0;
`;

const TabButton = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ active }) =>
      active ? "var(--Primary-Color-Shade-1)" : "transparent"};
  margin-bottom: -2px;
  padding: 8px 24px;
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  color: ${({ active }) =>
    active ? "var(--Primary-Color-Shade-1)" : "#1a202c"};
  cursor: pointer;
  transition:
    color 0.15s,
    border-color 0.15s;

  &:hover {
    color: var(--Primary-Color-Shade-1);
  }
`;

const TilesWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 12px;
  max-height: 400px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--Primary-Color-Shade-1);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: #e0e0e0;
  }
`;

const Tile = styled.div`
  width: 150px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #f6f7f9;
  cursor: default;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 5px;
  text-align: left;

  .tile-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
  }

  .tile-title {
    font-weight: 600;
    font-size: 14px;
    color: #000;
  }

  .tile-date {
    font-size: 11px;
    color: #1a202c;
  }
`;

const PqsTile = styled.div`
  width: 230px;
  padding: 10px 12px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0;
  cursor: default;
  text-align: left;

  .tile-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
  }

  .tile-title {
    font-weight: 600;
    font-size: 13px;
    color: #000;
  }

  .tile-row {
    font-size: 11px;
    color: #1a202c;
    line-height: 1.3;
    margin: 0;
    padding: 0;
  }
`;

const PopupBody = styled.div`
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  text-align: left;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--Primary-Color-Shade-1);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const LastRefreshedLabel = styled.div`
  font-size: 11px;
  color: #4a5568;
  text-align: left;
  margin-bottom: 12px;
  margin-top: -8px;
`;

const RefreshButton = styled.button<{ warning?: boolean }>`
  background: ${({ warning }) =>
    warning
      ? "rgba(0, 0, 0, 0.08)"
      : "var(--soft-primary-color, #0d6ab2)"};
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ warning }) =>
    warning ? "#78350f" : "var(--Primary-Color-Shade-1, #0d6ab2)"};
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ warning }) =>
      warning
        ? "rgba(0, 0, 0, 0.15)"
        : "var(--light-primary-color, #e8f4fd)"};
  }
`;

const TableWrapper = styled.div`
  max-height: 250px;
  overflow-y: auto;
  margin: 8px auto 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  width: 80%;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 10px 16px;
    border-bottom: 1px solid #eee;
    text-align: left;
    font-size: 14px;
  }

  th {
    background: color-mix(
      in srgb,
      var(--Primary-Color-Shade-1) 70%,
      white
    );
    color: #fff;
    font-weight: 600;
  }
`;

const ModalBackdrop = styled.div<{ topRight?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ topRight }) =>
    topRight ? "transparent" : "rgba(0, 0, 0, 0.04)"};
  display: flex;
  align-items: ${({ topRight }) => (topRight ? "flex-start" : "center")};
  justify-content: ${({ topRight }) => (topRight ? "flex-end" : "center")};
  padding: ${({ topRight }) => (topRight ? "72px 16px 0 0" : "0")};
  z-index: 1000;
  animation: ${({ topRight }) =>
    topRight ? "none" : "backdrop-fade-in 0.18s ease-out"};

  @keyframes backdrop-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PopupTitle = styled.h2<{ warning?: boolean; error?: boolean }>`
  margin: 0;
  flex: 1;
  font-size: 13px;
  font-weight: 700;
  color: ${({ warning }) => (warning ? "#78350f" : "#ffffff")};
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  font-family: var(--font-label);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div<{ topRight?: boolean; contentWidth?: string | number }>`
  position: relative;
  background: white;
  border-radius: 8px;
  width: ${({ contentWidth }) =>
    contentWidth
      ? typeof contentWidth === "number"
        ? `${contentWidth}px`
        : contentWidth
      : "560px"};
  max-height: 85vh;
  overflow-y: auto;
  text-align: center;
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.08),
    0 1px 4px rgba(0, 0, 0, 0.06);
  border: ${({ topRight }) => (topRight ? "1px solid #d0d7e2" : "none")};
  animation: ${({ topRight }) =>
    topRight
      ? "health-check-slide-in 0.25s ease"
      : "info-slide-in 0.18s ease-out"};
  display: flex;
  flex-direction: column;

  @keyframes info-slide-in {
    from {
      opacity: 0;
      transform: translateY(-12px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes health-check-slide-in {
    from {
      opacity: 0.4;
      transform: translateX(40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: var(--Primary-Color-Shade-1);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: #f0f4fa;
  }

  h3 {
    text-align: center !important;
  }
`;

const PopupHeader = styled.div<{ warning?: boolean; error?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  background: ${({ warning, error }) =>
    warning ? "#fbbf24" : error ? "#dc2626" : "var(--Primary-Color-Shade-1)"};
  border-radius: 8px 8px 0 0;
  flex-shrink: 0;
`;

const PopupHeaderSpacer = styled.div`
  width: 28px;
`;

const PopupHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CloseXButton = styled.button<{ warning?: boolean; error?: boolean }>`
  background: ${({ warning, error }) =>
    warning
      ? "rgba(0, 0, 0, 0.08)"
      : error
        ? "rgba(255, 255, 255, 0.18)"
        : "var(--soft-primary-color, #0d6ab2)"};
  border: none;
  color: ${({ warning, error }) =>
    warning ? "#78350f" : error ? "#ffffff" : "var(--Primary-Color-Shade-1, #0d6ab2)"};
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${({ warning, error }) =>
      warning
        ? "rgba(0, 0, 0, 0.15)"
        : error
          ? "rgba(255, 255, 255, 0.32)"
          : "var(--light-primary-color, #e8f4fd)"};
  }
`;

const InputContainer = styled.div<{ freeText: boolean }>`
  display: flex;
  align-items: ${({ freeText }) => (freeText ? "center" : "flex-start")};
  justify-content: ${({ freeText }) => (freeText ? "center" : "flex-start")};
  border: ${(props) => (props.freeText ? "none" : "1px solid #ccc")};
  border-radius: 4px;
  padding: 8px;
  margin: 15px auto;
  width: 80%;
  max-width: 500px; /* or whatever max you prefer */
  flex-wrap: wrap;
  overflow-wrap: break-word;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  box-sizing: border-box;
  text-align: ${({ freeText }) => (freeText ? "center" : "left")};

  p {
    flex: 1 1 100%;
    margin: 0;
    overflow-wrap: break-word;
    word-break: break-word;
    white-space: pre-wrap;
    max-width: 100%;
    box-sizing: border-box;
    text-align: ${({ freeText }) => (freeText ? "center" : "left")};
  }
`;

const IconWrapper = styled.div`
  position: relative;
  cursor: pointer;

  img {
    width: 16px;
    height: 16px;
    margin-left: 8px;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0.9;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 8px;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
  gap: 12px;
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #e0e0e0;
  border-top-color: var(--Primary-Color-Shade-1);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #1a202c;
`;

const CancelButton = styled.button`
  background: white;
  color: var(--Primary-Color-Shade-1, #0d6ab2);
  border: 1px solid var(--Primary-Color-Shade-1, #0d6ab2);
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-family: var(--font-label);
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--light-primary-color, #e8f4fd);
    color: var(--Primary-Color-Shade-1, #0d6ab2);
  }
`;

const ConfirmButton = styled.button<{ danger?: boolean }>`
  background: ${({ danger }) =>
    danger ? "#e53e3e" : "var(--Primary-Color-Shade-1)"};
  color: white;
  border: 1px solid
    ${({ danger }) => (danger ? "#e53e3e" : "var(--Primary-Color-Shade-1)")};
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ danger }) =>
      danger
        ? "#c53030"
        : "color-mix(in srgb, var(--Primary-Color-Shade-1) 85%, black)"};
    border-color: ${({ danger }) =>
      danger
        ? "#c53030"
        : "color-mix(in srgb, var(--Primary-Color-Shade-1) 85%, black)"};
  }
`;

const TextPre = styled.pre`
  background: #f6f8fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 12px;
  font-family: "Menlo", "Monaco", "Courier New", monospace;
  color: #1a202c;
  text-align: left;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 320px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 8px 0;
  width: 100%;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #b0b8c1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }
`;

const JsonPre = styled.pre`
  background: #f6f8fa;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 12px;
  font-family: "Menlo", "Monaco", "Courier New", monospace;
  color: #1a202c;
  text-align: left;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 320px;
  white-space: pre;
  word-break: normal;
  margin: 8px 0;
  width: 100%;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #b0b8c1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
  }
`;
